import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// 创建 axios 实例
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

// 请求拦截器 - 添加认证 token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 添加租户信息
    const currentTenant = localStorage.getItem('currentTenant');
    if (currentTenant) {
      config.headers['X-Tenant-Code'] = currentTenant;
      config.headers['X-Tenant-Id'] = currentTenant;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理认证错误
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    const status = error.response?.status;
    const code = error.response?.data?.code;
    const isRefreshCall = originalRequest?.url?.includes('/auth/refresh');

    // 刷新接口自身或明确的刷新过期/无效，直接退出
    if (status === 401 && (isRefreshCall || code === 'TOKEN_EXPIRED' || code === 'INVALID_REFRESH_TOKEN')) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('currentTenant');
      if (typeof document !== 'undefined') {
        document.location.href = '/login';
      }
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // 尝试刷新token
      const refreshSuccess = await authService.refreshToken();
      if (refreshSuccess) {
        // 重新发送原始请求
        return api(originalRequest);
      } else {
        // 刷新失败，清除本地存储并跳转到登录页
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('currentTenant');
        // 跳转到登录页
        if (typeof document !== 'undefined') {
          document.location.href = '/login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export interface LoginRequest {
  username: string;
  password: string;
  tenantCode?: string;
}

export interface User {
  id: string;
  username: string;
  isSuperAdmin: boolean;
  tenantCode?: string;
  tenants?: {
    id: number;
    code: string;
    name: string;
    role: string;
    isTenantAdmin: boolean;
  }[];
}

export interface LoginResponse {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
  user?: User;
  message?: string;
}

class AuthService {
  // 登录
  async login(request: LoginRequest): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/login', request);
    
    if (response.data.success && response.data.accessToken && response.data.user) {
      // 保存到本地存储
      localStorage.setItem('token', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken || '');
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // 设置当前租户优先级：user.tenant_code -> 保持现有currentTenant或default
      const existing = localStorage.getItem('currentTenant');
      const nextTenant = response.data.user.tenantCode || existing || 'default';
      localStorage.setItem('currentTenant', nextTenant);
    }
    
    return response.data;
  }

  // 登出
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // 忽略登出错误
    } finally {
      // 清除本地存储
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('currentTenant');
    }
  }

  // 获取当前用户信息
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await api.get<{ success: boolean; user?: User }>('/auth/me');
      return response.data.success ? response.data.user || null : null;
    } catch (error) {
      return null;
    }
  }

  // 刷新访问令牌
  async refreshToken(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        return false;
      }

      const response = await api.post<{ success: boolean; accessToken?: string; code?: string }>('/auth/refresh', {
        refreshToken
      });

      if (response.data.success && response.data.accessToken) {
        localStorage.setItem('token', response.data.accessToken);
        return true;
      }
      // 刷新失败，清理状态
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('currentTenant');
      return false;
    } catch (error: any) {
      const code = error.response?.data?.code;
      if (code === 'TOKEN_EXPIRED' || code === 'INVALID_REFRESH_TOKEN') {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('currentTenant');
      }
      return false;
    }
  }

  // 验证 token
  async verifyToken(): Promise<boolean> {
    try {
      await api.post('/auth/validate', {
        token: localStorage.getItem('token')
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  // 修改密码
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await api.post('/auth/change-password', {
      oldPassword,
      newPassword,
    });
  }

  // 获取本地存储的用户信息
  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  // 获取当前租户
  getCurrentTenant(): string | null {
    return localStorage.getItem('currentTenant');
  }

  // 设置当前租户
  setCurrentTenant(tenantCode: string): void {
    localStorage.setItem('currentTenant', tenantCode);
  }

  // 检查是否已登录
  isAuthenticated(): boolean {
    return !!localStorage.getItem('token');
  }

  // 检查是否是超级管理员
  isSuperAdmin(): boolean {
    const user = this.getStoredUser();
    return user?.isSuperAdmin || false;
  }

  // 检查是否是租户管理员
  isTenantAdmin(): boolean {
    const user = this.getStoredUser();
    const currentTenant = this.getCurrentTenant();
    if (!user || !currentTenant) return false;
    
    const tenant = user.tenants?.find(t => t.code === currentTenant);
    return tenant?.isTenantAdmin || false;
  }
}

export const authService = new AuthService();
export { api };

// 获取认证 headers
export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  
  const token = localStorage.getItem('token');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const currentTenant = localStorage.getItem('currentTenant');
  if (currentTenant) {
    headers['X-Tenant-Code'] = currentTenant;
  }
  
  return headers;
}
